import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Icons
import { MdOutlineFileDownload } from "react-icons/md";

// Components
import Button from '../../../../components/CustomButton';
import AddToModal from '../../../../components/AddTooModal';

// Styles
import styles from './player.module.scss';

// Types
import { UploadFile } from '../../../../types';
import FeedbackModal from '../../../../components/FeedbackModal';

// Context
import { useGenerateModalContext } from '../../context';

interface PlayerProps {
  video: UploadFile;
  onBack: () => void;
}

const Player: React.FC<PlayerProps> = ({ video, onBack }) => {
  // Hooks
  const { t } = useTranslation();

  // Context
  const { onCloseMainModal } = useGenerateModalContext();

  // State
  const [openAddToModal, setOpenAddToModal] = useState<boolean>(false);
  const [openFeedbackModal, setOpenFeedbackModal] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  // Handlers
  const handleAddTo = useCallback(() => {
    setOpenAddToModal(true);
  }, []);

  const handleCloseAddToModal = useCallback(() => {
    setOpenAddToModal(false);
  }, []);

  const handleAdded = useCallback(() => {
    setOpenAddToModal(false);
    setOpenFeedbackModal(true);
  }, []);

  const handleCloseFeedbackModal = useCallback(() => {
    setOpenFeedbackModal(false);
    onCloseMainModal();
  }, []);

  const handleLike = useCallback(() => {
    setOpenFeedbackModal(false);
    // TODO: Connect to the backend
    // try {
    //   const response = await fetch(urls.feedbackUrl(), {
    //     method: 'POST',
    //     headers: { 'CSRF-Token': csrfToken },
    //     body: data
    //   });
    // } catch (error) {
    //   console.error(error);
    // }

    // Ask if the flow is like this
    onCloseMainModal();
  }, []);

  const handleDislike = useCallback(() => {
    setOpenFeedbackModal(false);
    // TODO: Connect to the backend
    // try {
    //   const response = await fetch(urls.feedbackUrl(), {
    //     method: 'POST',
    //     headers: { 'CSRF-Token': csrfToken },
    //     body: data
    //   });
    // } catch (error) {
    //   console.error(error);
    // }
    onCloseMainModal();
  }, []);

  const handleDownload = useCallback(async () => {
    if (isDownloading) return; // Prevent multiple simultaneous downloads

    setIsDownloading(true);

    try {
      if (!video.url) {
        console.error('No video URL available for download');
        return;
      }

      // Create a safe filename from the video title or use a default
      const filename = video.title
        ? `${video.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.mp4`
        : `studypal_video_${Date.now()}.mp4`;

      // Try direct download first (for same-origin or CORS-enabled URLs)
      const link = document.createElement('a');
      link.href = video.url;
      link.download = filename;
      link.target = '_blank';

      // Check if we can download directly
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // If the above doesn't work due to CORS, we can fallback to fetching
      // But for most video URLs from external services, direct download should work


      // Small delay to show the loading state
      setTimeout(() => setIsDownloading(false), 1000);
    } catch (error) {
      console.error('Failed to download video:', error);
      // You might want to show a user-friendly error message here
      setIsDownloading(false);
    }
  }, [video.url, video.title, isDownloading]);

  return (
    <div className={styles.player}>
      <video src={video.url} controls className={styles.video} />

      <div className={styles.actions}>
        <Button variant="text" onClick={onBack} classes={{ root: styles.button_back }}>
          {t('generate.video.back')}
        </Button>

        <div className={styles.actions_right}>
          <Button
            variant="text"
            onClick={handleDownload}
            classes={{ root: styles.button_download }}
            icon={<MdOutlineFileDownload />}
            disabled={isDownloading}
          >
            {isDownloading ? `${t('globals.download')}...` : t('globals.download')}
          </Button>

          <Button variant="secondary" onClick={handleAddTo} classes={{ root: styles.button_add_to }}>
            {t('globals.add_to')}
          </Button>
        </div>
      </div>

      <AddToModal open={openAddToModal} onClose={handleCloseAddToModal} onAdded={handleAdded} />

      <FeedbackModal
        open={openFeedbackModal}
        onClose={handleCloseFeedbackModal}
        onLike={handleLike}
        onDislike={handleDislike}
        message={t('generate.video.feedback_message')}
      />
    </div>
  )
}

export default Player;
